import React from 'react';
import { useTranslation } from '../../i18n/i18n';
import { compassName, compassNames, WindRoseData } from '../../utils/wind';
import './WindRose.scss';

interface WindRoseProps {
  rose: WindRoseData;
  latestDirection?: number;
  meanSpeed?: number;
}

const pt = (deg: number, r: number) => {
  const a = (deg * Math.PI) / 180;
  return `${(r * Math.sin(a)).toFixed(1)} ${(-r * Math.cos(a)).toFixed(1)}`;
};

const WindRose = ({ rose, latestDirection, meanSpeed }: WindRoseProps) => {
  const { t } = useTranslation('weather');
  const { t: tc } = useTranslation('common');
  const max = Math.max(...rose.counts);
  const petals = rose.counts
    .map((c, k) => {
      if (!c) return null;
      const r = 14 + (76 * c) / max;
      const a = k * 22.5;
      return {
        key: k,
        d: `M0 0L${pt(a - 9.5, r)}A${r.toFixed(1)} ${r.toFixed(1)} 0 0 1 ${pt(a + 9.5, r)}Z`,
        op: (0.45 + (0.55 * c) / max).toFixed(2),
      };
    })
    .filter(Boolean) as Array<{ key: number; d: string; op: string }>;
  const share = rose.counts
    .map((c, k) => (c ? `${compassNames[k]} ${Math.round((100 * c) / Math.max(rose.total, 1))}%` : null))
    .filter(Boolean)
    .join(', ');

  return (
    <article className="WindRose">
      <div className="WindRose__head">
        <h2>
          {t('rose.title')} <span>({t('rose.unit')})</span>
        </h2>
        {rose.prevailing && (
          <span className="WindRose__prevailing">
            {t('rose.prevailing', { dir: rose.prevailing.name, pct: rose.prevailing.pct })}
          </span>
        )}
      </div>
      <div className="WindRose__body">
        <svg
          viewBox="-115 -115 230 230"
          width="220"
          height="220"
          role="img"
          aria-label={t('rose.aria', { share: share || tc('empty') })}
        >
          <circle cx="0" cy="0" r="35" fill="none" stroke="#E3E8F0" />
          <circle cx="0" cy="0" r="62" fill="none" stroke="#E3E8F0" />
          <circle cx="0" cy="0" r="90" fill="none" stroke="#C9D2E0" />
          <line x1="0" y1="-90" x2="0" y2="90" stroke="#E3E8F0" />
          <line x1="-90" y1="0" x2="90" y2="0" stroke="#E3E8F0" />
          {petals.map((p) => (
            <path key={p.key} d={p.d} fill="#1F6FD1" fillOpacity={p.op} stroke="#FFFFFF" strokeWidth="2" />
          ))}
          <text x="0" y="-100" textAnchor="middle" fontSize="12" fontWeight="600" fill="#2D3748">
            N
          </text>
          <text x="104" y="4" textAnchor="middle" fontSize="12" fontWeight="600" fill="#2D3748">
            E
          </text>
          <text x="0" y="110" textAnchor="middle" fontSize="12" fontWeight="600" fill="#2D3748">
            S
          </text>
          <text x="-104" y="4" textAnchor="middle" fontSize="12" fontWeight="600" fill="#2D3748">
            W
          </text>
        </svg>
        <dl>
          <dt>{t('rose.latest')}</dt>
          <dd>{latestDirection === undefined ? tc('empty') : `${latestDirection}° ${compassName(latestDirection)}`}</dd>
          <dt>{t('rose.meanSpeed')}</dt>
          <dd>{meanSpeed === undefined ? tc('empty') : `${meanSpeed.toFixed(1)} m/s`}</dd>
          <dt>{t('rose.calm')}</dt>
          <dd>{t('rose.calmValue', { calm: rose.calm, total: rose.total })}</dd>
        </dl>
      </div>
    </article>
  );
};

export default WindRose;
